import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CATEGORY_ORDER, getCategoryMeta } from './categoryMeta';

export type StatusFilter = 'all' | 'unread' | 'starred';
export type DateBucket = 'today' | 'yesterday' | 'thisWeek' | 'earlier';

interface Counts {
    all: number;
    unread: number;
    starred: number;
    byCategory: Record<string, number>;
    byDate: Record<DateBucket, number>;
}

interface Props {
    counts: Counts;
    status: StatusFilter;
    category: string | null;
    dateBucket: DateBucket | null;
    onStatusChange: (status: StatusFilter) => void;
    onCategoryChange: (category: string | null) => void;
    onDateBucketChange: (bucket: DateBucket | null) => void;
}

const DATE_BUCKETS: { key: DateBucket; labelId: string; defaultLabel: string }[] = [
    { key: 'today',     labelId: 'notifications.archive.groupToday',     defaultLabel: 'Today' },
    { key: 'yesterday', labelId: 'notifications.archive.groupYesterday', defaultLabel: 'Yesterday' },
    { key: 'thisWeek',  labelId: 'notifications.archive.groupThisWeek',  defaultLabel: 'This week' },
    { key: 'earlier',   labelId: 'notifications.archive.groupEarlier',   defaultLabel: 'Earlier' },
];

/**
 * Left-rail filter panel — three independent bucket groups (Statusi, Lloji,
 * Data). Each is a single-select within its section; selecting "none" in a
 * section means that dimension is unfiltered. The active bucket is highlighted
 * with the navy fill; the count chip on the right shows live counts derived
 * from the loaded item set (not a separate API call — the inbox is capped at
 * 50 items by design so client-side aggregation is cheap and accurate).
 */
const NotificationSidebar: React.FC<Props> = ({
    counts,
    status,
    category,
    dateBucket,
    onStatusChange,
    onCategoryChange,
    onDateBucketChange,
}) => {
    const intl = useIntl();

    const renderBucket = (
        active: boolean,
        onClick: () => void,
        label: React.ReactNode,
        count: number,
        leadingDot?: string,
    ) => (
        <button
            type="button"
            className={`notif-sidebar__bucket ${active ? 'is-active' : ''}`}
            onClick={onClick}
        >
            {leadingDot && (
                <span
                    className="notif-sidebar__bucket-dot"
                    style={{ background: leadingDot }}
                    aria-hidden="true"
                />
            )}
            <span className="notif-sidebar__bucket-label">{label}</span>
            <span className="notif-sidebar__bucket-count">{count}</span>
        </button>
    );

    return (
        <aside className="notif-sidebar" aria-label={intl.formatMessage({ id: 'notifications.sidebar.label', defaultMessage: 'Filters' })}>
            {/* ── Statusi ── */}
            <div className="notif-sidebar__section">
                <h3 className="notif-sidebar__title">
                    <FormattedMessage id="notifications.sidebar.status" defaultMessage="Status" />
                </h3>
                {renderBucket(
                    status === 'all',
                    () => onStatusChange('all'),
                    <FormattedMessage id="notifications.sidebar.statusAll" defaultMessage="All" />,
                    counts.all,
                )}
                {renderBucket(
                    status === 'unread',
                    () => onStatusChange('unread'),
                    <FormattedMessage id="notifications.archive.filterUnread" defaultMessage="Unread" />,
                    counts.unread,
                )}
                {renderBucket(
                    status === 'starred',
                    () => onStatusChange('starred'),
                    <FormattedMessage id="notifications.archive.filterStarred" defaultMessage="Starred" />,
                    counts.starred,
                )}
            </div>

            {/* ── Lloji (category) ── */}
            <div className="notif-sidebar__section">
                <h3 className="notif-sidebar__title">
                    <FormattedMessage id="notifications.sidebar.type" defaultMessage="Type" />
                </h3>
                {renderBucket(
                    category === null,
                    () => onCategoryChange(null),
                    <FormattedMessage id="notifications.sidebar.typeAll" defaultMessage="All types" />,
                    counts.all,
                )}
                {CATEGORY_ORDER.map((cat) => {
                    const meta = getCategoryMeta(cat);
                    const count = counts.byCategory[cat] || 0;
                    // Hide buckets the user has zero of — keeps the panel from
                    // showing 7 zero-rows for a fresh inbox.
                    if (count === 0 && category !== cat) return null;
                    return (
                        <React.Fragment key={cat}>
                            {renderBucket(
                                category === cat,
                                () => onCategoryChange(category === cat ? null : cat),
                                <FormattedMessage id={meta.labelId} defaultMessage={meta.defaultLabel} />,
                                count,
                                meta.color,
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* ── Data ── */}
            <div className="notif-sidebar__section">
                <h3 className="notif-sidebar__title">
                    <FormattedMessage id="notifications.sidebar.date" defaultMessage="Date" />
                </h3>
                {renderBucket(
                    dateBucket === null,
                    () => onDateBucketChange(null),
                    <FormattedMessage id="notifications.sidebar.dateAll" defaultMessage="Any time" />,
                    counts.all,
                )}
                {DATE_BUCKETS.map((b) => {
                    const count = counts.byDate[b.key] || 0;
                    if (count === 0 && dateBucket !== b.key) return null;
                    return (
                        <React.Fragment key={b.key}>
                            {renderBucket(
                                dateBucket === b.key,
                                () => onDateBucketChange(dateBucket === b.key ? null : b.key),
                                <FormattedMessage id={b.labelId} defaultMessage={b.defaultLabel} />,
                                count,
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </aside>
    );
};

export default NotificationSidebar;
